import React, { useState, useEffect } from 'react';
import { authAPI, vendorAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const inputStyle = {
  width:'100%',
  background:'var(--surface2)',
  border:'1px solid var(--border2)',
  color:'var(--text)',
  padding:'10px 14px',
  borderRadius:'8px',
  fontSize:'13.5px',
  outline:'none',
};

const labelStyle = {
  display:'block',
  fontSize:'11px',
  fontFamily:'DM Mono,monospace',
  color:'var(--text3)',
  letterSpacing:'1px',
  textTransform:'uppercase',
  marginBottom:'6px',
};

const Account = () => {
  const { user } = useAuth();
  const isOwner = user?.role === 'vendor_admin';

  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState({
    business_name:'', owner_name:'',
    email:'', phone:'', alternate_phone:'',
    gst_number:'', address:'', city:'',
    state:'', pincode:'', business_type:'',
    num_engineers:'',
  });
  const [supervisorForm, setSupervisorForm] = useState({
    name:'', phone:'',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  const [verificationNeeded, setVerificationNeeded] = useState(false);
  const [otpInput, setOtpInput] = useState('');
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSuccess, setOtpSuccess] = useState('');
  const [resendingOtp, setResendingOtp] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    current_password:'', new_password:'',
    confirm_password:'',
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      if (isOwner) {
        const res = await vendorAPI.getProfile();
        const v = res.data.data.vendor;
        setProfileForm({
          business_name: v.business_name || '',
          owner_name: v.owner_name || '',
          email: v.email || '',
          phone: v.phone || '',
          alternate_phone: v.alternate_phone || '',
          gst_number: v.gst_number || '',
          address: v.address || '',
          city: v.city || '',
          state: v.state || '',
          pincode: v.pincode || '',
          business_type: v.business_type || '',
          num_engineers: v.num_engineers || '',
        });
      } else {
        const res = await authAPI.getProfile();
        const p = res.data.data;
        setSupervisorForm({
          name: p.name || '',
          phone: p.phone || '',
        });
      }
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileError('');
    setProfileSuccess('');
    setSavingProfile(true);
    try {
      if (isOwner) {
        const res = await vendorAPI.updateProfile(profileForm);
        if (res.data.data.verification_required) {
          setVerificationNeeded(true);
          setOtpError('');
          setOtpSuccess('');
        } else {
          setProfileSuccess('Saved successfully.');
        }
      } else {
        await vendorAPI.updateUser(user.id, supervisorForm);
        setProfileSuccess('Saved successfully.');
      }
    } catch (err) {
      setProfileError(
        err.response?.data?.message
        || 'Failed to save changes');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleVerifyEmail = async () => {
    setOtpError('');
    setOtpSuccess('');
    if (!otpInput || otpInput.trim().length !== 6) {
      setOtpError('Enter the 6-digit code from your email');
      return;
    }
    setVerifyingOtp(true);
    try {
      await authAPI.verifyOtp({
        email: profileForm.email,
        otp: otpInput.trim(),
      });
      setOtpSuccess('Email verified! You\u2019re all set.');
      setVerificationNeeded(false);
      setOtpInput('');
    } catch (err) {
      setOtpError(
        err.response?.data?.message
        || 'Invalid or expired code');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpError('');
    setOtpSuccess('');
    setResendingOtp(true);
    try {
      await authAPI.resendOtp({ email: profileForm.email });
      setOtpSuccess('A new code has been sent.');
    } catch (err) {
      setOtpError(
        err.response?.data?.message
        || 'Failed to resend code');
    } finally {
      setResendingOtp(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.current_password || !passwordForm.new_password) {
      setPasswordError('Please fill in both password fields');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      setPasswordError('New password must be at least 8 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New password and confirmation do not match');
      return;
    }

    setSavingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordSuccess('Password changed successfully.');
      setPasswordForm({
        current_password:'', new_password:'',
        confirm_password:'',
      });
    } catch (err) {
      setPasswordError(
        err.response?.data?.message
        || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        padding:'40px', textAlign:'center',
        color:'var(--text3)',
        fontFamily:'DM Mono,monospace',
      }}>
        Loading account...
      </div>
    );
  }

  return (
    <div style={{ padding:'24px', maxWidth:'640px' }}>
      <div style={{ marginBottom:'24px' }}>
        <h1 style={{
          fontFamily:'Syne,sans-serif',
          fontSize:'22px', fontWeight:'800',
        }}>
          My Account
        </h1>
        <p style={{
          color:'var(--text3)',
          fontSize:'13px', marginTop:'4px',
        }}>
          {isOwner
            ? 'Manage your business profile and login'
            : 'Manage your account details and login'}
        </p>
      </div>

      {/* Profile Section */}
      <div style={{
        background:'var(--surface)',
        border:'1px solid var(--border)',
        borderRadius:'12px',
        padding:'24px',
        marginBottom:'20px',
      }}>
        <div style={{
          fontFamily:'Syne,sans-serif',
          fontSize:'15px', fontWeight:'700',
          marginBottom:'20px',
        }}>
          {isOwner ? 'Business Profile' : 'Your Details'}
        </div>

        {isOwner ? (
          <div style={{
            display:'grid',
            gridTemplateColumns:'1fr 1fr',
            gap:'16px',
          }}>
            <div>
              <label style={labelStyle}>Business Name</label>
              <input style={inputStyle}
                value={profileForm.business_name}
                onChange={e => setProfileForm({
                  ...profileForm,
                  business_name:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>Owner Name</label>
              <input style={inputStyle}
                value={profileForm.owner_name}
                onChange={e => setProfileForm({
                  ...profileForm,
                  owner_name:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>Email (login ID)</label>
              <input type="email" style={inputStyle}
                value={profileForm.email}
                onChange={e => setProfileForm({
                  ...profileForm,
                  email:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input style={inputStyle}
                value={profileForm.phone}
                onChange={e => setProfileForm({
                  ...profileForm,
                  phone:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>Alternate Phone</label>
              <input style={inputStyle}
                value={profileForm.alternate_phone}
                onChange={e => setProfileForm({
                  ...profileForm,
                  alternate_phone:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>GST Number</label>
              <input style={inputStyle}
                value={profileForm.gst_number}
                onChange={e => setProfileForm({
                  ...profileForm,
                  gst_number:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>City</label>
              <input style={inputStyle}
                value={profileForm.city}
                onChange={e => setProfileForm({
                  ...profileForm,
                  city:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>State</label>
              <input style={inputStyle}
                value={profileForm.state}
                onChange={e => setProfileForm({
                  ...profileForm,
                  state:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>Pincode</label>
              <input style={inputStyle}
                value={profileForm.pincode}
                onChange={e => setProfileForm({
                  ...profileForm,
                  pincode:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>No. of Engineers</label>
              <input type="number" min="1" style={inputStyle}
                value={profileForm.num_engineers}
                onChange={e => setProfileForm({
                  ...profileForm,
                  num_engineers:e.target.value
                })}
              />
            </div>
            <div style={{ gridColumn:'1 / -1' }}>
              <label style={labelStyle}>Address</label>
              <input style={inputStyle}
                value={profileForm.address}
                onChange={e => setProfileForm({
                  ...profileForm,
                  address:e.target.value
                })}
              />
            </div>
          </div>
        ) : (
          <div style={{
            display:'grid',
            gridTemplateColumns:'1fr 1fr',
            gap:'16px',
          }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input style={inputStyle}
                value={supervisorForm.name}
                onChange={e => setSupervisorForm({
                  ...supervisorForm,
                  name:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input style={inputStyle}
                value={supervisorForm.phone}
                onChange={e => setSupervisorForm({
                  ...supervisorForm,
                  phone:e.target.value
                })}
              />
            </div>
          </div>
        )}

        {profileError && (
          <div style={{
            background:'rgba(255,77,109,0.1)',
            border:'1px solid rgba(255,77,109,0.3)',
            color:'var(--red)',
            padding:'10px 14px',
            borderRadius:'8px',
            fontSize:'13px',
            marginTop:'16px',
          }}>
            {profileError}
          </div>
        )}
        {profileSuccess && (
          <div style={{
            background:'rgba(0,212,133,0.1)',
            border:'1px solid rgba(0,212,133,0.3)',
            color:'var(--green)',
            padding:'10px 14px',
            borderRadius:'8px',
            fontSize:'13px',
            marginTop:'16px',
          }}>
            {profileSuccess}
          </div>
        )}

        <button
          onClick={handleSaveProfile}
          disabled={savingProfile}
          style={{
            marginTop:'20px',
            background:'var(--accent)',
            color:'var(--bg)',
            border:'none',
            padding:'10px 24px',
            borderRadius:'8px',
            fontSize:'13px',
            fontWeight:'600',
            cursor: savingProfile
              ? 'not-allowed' : 'pointer',
          }}
        >
          {savingProfile ? 'Saving...' : 'Save Changes'}
        </button>

        {verificationNeeded && (
          <div style={{
            marginTop:'20px',
            padding:'16px',
            background:'rgba(255,193,7,0.08)',
            border:'1px solid rgba(255,193,7,0.3)',
            borderRadius:'10px',
          }}>
            <div style={{
              fontSize:'13px',
              fontWeight:'600',
              color:'var(--yellow)',
              marginBottom:'6px',
            }}>
              Verify your new email
            </div>
            <div style={{
              fontSize:'12.5px',
              color:'var(--text2)',
              marginBottom:'14px',
            }}>
              We\u2019ve sent a 6-digit code to{' '}
              <strong>{profileForm.email}</strong>. You
              won\u2019t be able to log in with this email
              until it\u2019s verified.
            </div>
            <div style={{
              display:'flex', gap:'10px',
              flexWrap:'wrap',
            }}>
              <input
                value={otpInput}
                onChange={e => setOtpInput(
                  e.target.value.replace(/\D/g,'').slice(0,6))}
                placeholder="6-digit code"
                style={{
                  ...inputStyle,
                  width:'140px',
                  fontFamily:'DM Mono,monospace',
                  letterSpacing:'2px',
                }}
              />
              <button
                onClick={handleVerifyEmail}
                disabled={verifyingOtp}
                style={{
                  background:'var(--accent)',
                  color:'var(--bg)',
                  border:'none',
                  padding:'0 20px',
                  borderRadius:'8px',
                  fontSize:'13px',
                  fontWeight:'600',
                  cursor: verifyingOtp
                    ? 'not-allowed' : 'pointer',
                }}
              >
                {verifyingOtp ? 'Verifying...' : 'Verify'}
              </button>
              <button
                onClick={handleResendOtp}
                disabled={resendingOtp}
                style={{
                  background:'transparent',
                  color:'var(--text2)',
                  border:'1px solid var(--border2)',
                  padding:'0 16px',
                  borderRadius:'8px',
                  fontSize:'13px',
                  cursor: resendingOtp
                    ? 'not-allowed' : 'pointer',
                }}
              >
                {resendingOtp ? 'Sending...' : 'Resend Code'}
              </button>
            </div>
            {otpError && (
              <div style={{
                color:'var(--red)',
                fontSize:'12px',
                marginTop:'10px',
              }}>
                {otpError}
              </div>
            )}
            {otpSuccess && (
              <div style={{
                color:'var(--green)',
                fontSize:'12px',
                marginTop:'10px',
              }}>
                {otpSuccess}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Password Section */}
      <div style={{
        background:'var(--surface)',
        border:'1px solid var(--border)',
        borderRadius:'12px',
        padding:'24px',
      }}>
        <div style={{
          fontFamily:'Syne,sans-serif',
          fontSize:'15px', fontWeight:'700',
          marginBottom:'20px',
        }}>
          Change Password
        </div>

        <div style={{ marginBottom:'16px' }}>
          <label style={labelStyle}>Current Password</label>
          <input type="password" style={inputStyle}
            value={passwordForm.current_password}
            onChange={e => setPasswordForm({
              ...passwordForm,
              current_password:e.target.value
            })}
          />
        </div>
        <div style={{ marginBottom:'16px' }}>
          <label style={labelStyle}>New Password</label>
          <input type="password" style={inputStyle}
            placeholder="At least 8 characters"
            value={passwordForm.new_password}
            onChange={e => setPasswordForm({
              ...passwordForm,
              new_password:e.target.value
            })}
          />
        </div>
        <div style={{ marginBottom:'8px' }}>
          <label style={labelStyle}>Confirm New Password</label>
          <input type="password" style={inputStyle}
            value={passwordForm.confirm_password}
            onChange={e => setPasswordForm({
              ...passwordForm,
              confirm_password:e.target.value
            })}
          />
        </div>

        {passwordError && (
          <div style={{
            background:'rgba(255,77,109,0.1)',
            border:'1px solid rgba(255,77,109,0.3)',
            color:'var(--red)',
            padding:'10px 14px',
            borderRadius:'8px',
            fontSize:'13px',
            marginTop:'12px',
          }}>
            {passwordError}
          </div>
        )}
        {passwordSuccess && (
          <div style={{
            background:'rgba(0,212,133,0.1)',
            border:'1px solid rgba(0,212,133,0.3)',
            color:'var(--green)',
            padding:'10px 14px',
            borderRadius:'8px',
            fontSize:'13px',
            marginTop:'12px',
          }}>
            {passwordSuccess}
          </div>
        )}

        <button
          onClick={handleChangePassword}
          disabled={savingPassword}
          style={{
            marginTop:'20px',
            background:'transparent',
            color:'var(--accent)',
            border:'1px solid var(--accent)',
            padding:'10px 24px',
            borderRadius:'8px',
            fontSize:'13px',
            fontWeight:'600',
            cursor: savingPassword
              ? 'not-allowed' : 'pointer',
          }}
        >
          {savingPassword ? 'Changing...' : 'Change Password'}
        </button>
      </div>
    </div>
  );
};

export default Account;
